/**
 * Key-value storage backend for the offline store (Phase 16A, design §3.4 platform adapter).
 * The web build uses IndexedDB; tests use memory; a Capacitor shell could supply SQLCipher.
 * Values handed to a backend are already encrypted, except the `meta` store (no PHI).
 */

// Phase 18 M10 adds `drafts` (chart form autosave); DB_VERSION 2 creates it on existing devices
export const OFFLINE_STORES = ['meta', 'profile', 'session', 'packets', 'visits', 'chartEntries', 'assessments', 'outbox', 'blobs', 'escrow', 'drafts'] as const;
export type OfflineStoreName = typeof OFFLINE_STORES[number];

export interface KvBackend {
  get<T>(store: OfflineStoreName, key: string): Promise<T | undefined>;
  getAll<T>(store: OfflineStoreName): Promise<Array<{ key: string; value: T }>>;
  put(store: OfflineStoreName, key: string, value: unknown): Promise<void>;
  delete(store: OfflineStoreName, key: string): Promise<void>;
  clear(store: OfflineStoreName): Promise<void>;
  /** Delete the whole database. */
  destroy(): Promise<void>;
}

export type KvBackendFactory = (dbName: string) => Promise<KvBackend>;

const DB_VERSION = 2;

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** IndexedDB backend: one object store per offline store, records kept as { key, value }. */
export const indexedDbBackend: KvBackendFactory = (dbName) => new Promise((resolve, reject) => {
  if (typeof indexedDB === 'undefined') {
    reject(new Error('IndexedDB is not available'));
    return;
  }
  const open = indexedDB.open(dbName, DB_VERSION);
  open.onupgradeneeded = () => {
    OFFLINE_STORES.forEach((name) => {
      if (!open.result.objectStoreNames.contains(name)) open.result.createObjectStore(name, { keyPath: 'key' });
    });
  };
  open.onerror = () => reject(open.error);
  open.onsuccess = () => {
    const db = open.result;
    const run = <T>(store: OfflineStoreName, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> =>
      promisify(fn(db.transaction(store, mode).objectStore(store)));
    resolve({
      async get<T>(store: OfflineStoreName, key: string) {
        const row = await run<{ key: string; value: T } | undefined>(store, 'readonly', (s) => s.get(key));
        return row?.value;
      },
      async getAll<T>(store: OfflineStoreName) {
        return run<Array<{ key: string; value: T }>>(store, 'readonly', (s) => s.getAll());
      },
      async put(store, key, value) { await run(store, 'readwrite', (s) => s.put({ key, value })); },
      async delete(store, key) { await run(store, 'readwrite', (s) => s.delete(key)); },
      async clear(store) { await run(store, 'readwrite', (s) => s.clear()); },
      destroy: () => new Promise<void>((done, fail) => {
        db.close();
        const del = indexedDB.deleteDatabase(dbName);
        del.onsuccess = () => done();
        del.onerror = () => fail(del.error);
        del.onblocked = () => done(); // another tab still has it open; it is deleted when that tab closes
      }),
    });
  };
});

/**
 * The Phase 9B draft queue database (`pelora-drafts`, unencrypted, store `chartEntries`). 16C
 * (decision P3) only reads it to migrate drafts into the encrypted outbox, then deletes it.
 * Kept here so `indexedDB.open(` appears in this file only (CI guard).
 */
export interface LegacyDraftRecord {
  id: string;
  userId: string;
  patientId: string;
  visitId: string;
  payload: unknown;
  createdAt: string;
  failedReason?: string;
}

export interface LegacyDraftStore {
  list(): Promise<LegacyDraftRecord[]>;
  remove(id: string): Promise<void>;
  destroy(): Promise<void>;
}

export const LEGACY_DRAFTS_DB = 'pelora-drafts';

export async function openLegacyDrafts(): Promise<LegacyDraftStore | null> {
  if (typeof indexedDB === 'undefined') return null;
  // Never create the database: databases() lists it when supported, else open and drop an empty one
  const listing = (indexedDB as IDBFactory & { databases?: () => Promise<Array<{ name?: string }>> }).databases;
  if (typeof listing === 'function') {
    try {
      const dbs = await listing.call(indexedDB);
      if (!dbs.some((d) => d.name === LEGACY_DRAFTS_DB)) return null;
    } catch {
      // fall through to open
    }
  }
  const db = await new Promise<IDBDatabase | null>((resolve) => {
    try {
      const req = indexedDB.open(LEGACY_DRAFTS_DB);
      req.onupgradeneeded = () => { /* did not exist: leave it empty, destroyed below */ };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  if (!db) return null;
  const destroy = () => new Promise<void>((done) => {
    db.close();
    const del = indexedDB.deleteDatabase(LEGACY_DRAFTS_DB);
    del.onsuccess = () => done();
    del.onerror = () => done();
    del.onblocked = () => done();
  });
  if (!db.objectStoreNames.contains('chartEntries')) {
    await destroy();
    return null;
  }
  const run = <T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>) =>
    promisify(fn(db.transaction('chartEntries', mode).objectStore('chartEntries')));
  return {
    list: async () => (await run<LegacyDraftRecord[]>('readonly', (s) => s.getAll())) || [],
    remove: async (id) => { await run('readwrite', (s) => s.delete(id)); },
    destroy,
  };
}

/** In-memory backend (tests, and browsers without IndexedDB where offline mode is not offered). */
export function createMemoryBackendFactory(): KvBackendFactory {
  const databases = new Map<string, Map<string, Map<string, unknown>>>();
  return async (dbName) => {
    const db = () => {
      let found = databases.get(dbName);
      if (!found) {
        found = new Map(OFFLINE_STORES.map((s) => [s, new Map<string, unknown>()]));
        databases.set(dbName, found);
      }
      return found;
    };
    const table = (store: OfflineStoreName) => db().get(store) as Map<string, unknown>;
    return {
      async get<T>(store: OfflineStoreName, key: string) { return table(store).get(key) as T | undefined; },
      async getAll<T>(store: OfflineStoreName) { return Array.from(table(store).entries()).map(([key, value]) => ({ key, value: value as T })); },
      async put(store, key, value) { table(store).set(key, value); },
      async delete(store, key) { table(store).delete(key); },
      async clear(store) { table(store).clear(); },
      async destroy() { databases.delete(dbName); },
    };
  };
}
